const merge = require('lodash.merge');
const { toEnum, permission, permissionsToRoleMap } = require('./utils');
const {
  ONE_TO_ONE_ROLES: {
    CALLS_OWNER,
    CALLS_MANAGER,
    CALLS_DEVELOPER,
    CALLS_USER_ADMIN,
    CALLS_AGENT,
  },
  ONE_TO_MANY_ROLES: {
    SHOWS_ADMIN,
    SHOWS_OWNER,
    SHOW_CREATOR,
    HOST,
    MANAGE_OWN_SHOWS,
    MODERATOR,
  },
  ONE_TO_MANY_ROLES,
  ONE_TO_ONE_ROLES,
  BACKOFFICE_ROLES,
  VOD_ROLES,
  CHAT_ROLES,
} = require('./roles');

const ROLES = { ...ONE_TO_MANY_ROLES, ...ONE_TO_ONE_ROLES, ...VOD_ROLES, ...CHAT_ROLES };

const {
  READ,
} = require('./access');

const ONE_TO_ONE_PERMISSIONS = toEnum(
  'CALLS_AGENT_TOOL',
  'CALLS_DEVELOPER_PAGE',
  'CALL_LIST',
  'CALL_SETTINGS',
  'CALL_STATS',
  'CALL_STATS_LEADERBOARD',
  'CALLS_OPENING_HOURS',
  'QUEUES_MANAGEMENT',
  'EDIT_USERS_ONE_TO_ONE',
  'INTEGRATIONS_ONE_TO_ONE',
  'BILLING_ONE_TO_ONE',
  'LOCALIZATION_ONE_TO_ONE',
);

const ONE_TO_MANY_PERMISSIONS = toEnum(
  'SHOW_STATS',
  'LANGUAGE',
  'SHOWS',
  'MODERATE',
  'GO_LIVE_IN_APP',
  'MANAGE_OWN_SHOWS',
  'EDIT_USERS_ONE_TO_MANY',
  'INTEGRATIONS_ONE_TO_MANY',
  'CHAT_TERMS',
  'BILLING_ONE_TO_MANY',
  'DATA_EXPORT_SERVICE_CONFIGURATION',
  'DATA_EXPORT_SERVICE_EXPORT_JOBS',
  'MANAGE_AI_GROUNDINGS',
  'MANAGE_AI_GENERATIVES',
  'LOCALIZATION_ONE_TO_MANY',
  'MODERATE_CHAT_ONE_TO_MANY',
  'HIGHLIGHT_ONE_TO_MANY',
  'MANAGE_GAMES_ONE_TO_MANY',
  'SHOWS_ACTIONS_MANAGE',
);

const VOD_PERMISSIONS = toEnum(
  'VOD_MANAGE',
  'EDIT_USERS_VOD',
  'VOD_DISTRIBUTE',
  'LOCALIZATION_VOD',
  'VOD_STATS',
  'VOD_SETTINGS',
  'VOD_INTEGRATIONS',
  'VOD_ACTIONS_MANAGE',
);

const CHAT_PERMISSIONS = toEnum(
  'CHAT_STATS',
  'EDIT_USERS_CHAT',
  'CHAT_SETTINGS',
  'CHAT_MANAGE_STORES',
  'CHAT_CMS_POSTS',
  'CHAT_APP',
);

/**
 * Excludes
 *  All permissions in ONE_TO_ONE_PERMISSIONS except BILLING_ONE_TO_ONE
 * 'GO_LIVE_IN_APP',
 * 'MANAGE_OWN_SHOWS',
 */
const BILLING_PERMISSIONS = toEnum(
  'SHOW_STATS',
  'LANGUAGE',
  'SHOWS',
  'MODERATE',
  'EDIT_USERS_ONE_TO_MANY',
  'INTEGRATIONS_ONE_TO_MANY',
  'CHAT_TERMS',
  'BILLING_ONE_TO_MANY',
  'DATA_EXPORT_SERVICE_CONFIGURATION',
  'DATA_EXPORT_SERVICE_EXPORT_JOBS',
  'BILLING_ONE_TO_ONE',
);

// Product-agnostic permissions
const GENERAL_PERMISSIONS = toEnum(
  'WORKSPACE_SETTINGS',
);

const DEFAULT_ROLE_PERMISSIONS_ONE_TO_ONE = {
  [CALLS_OWNER]: {
    ...permission(ONE_TO_ONE_PERMISSIONS.CALLS_DEVELOPER_PAGE),
    ...permission(ONE_TO_ONE_PERMISSIONS.QUEUES_MANAGEMENT),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALLS_OPENING_HOURS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_SETTINGS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_STATS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_STATS_LEADERBOARD),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_LIST),
    ...permission(ONE_TO_ONE_PERMISSIONS.EDIT_USERS_ONE_TO_ONE),
    ...permission(ONE_TO_ONE_PERMISSIONS.INTEGRATIONS_ONE_TO_ONE),
    ...permission(ONE_TO_ONE_PERMISSIONS.BILLING_ONE_TO_ONE),
    ...permission(ONE_TO_ONE_PERMISSIONS.LOCALIZATION_ONE_TO_ONE),
    ...permission(GENERAL_PERMISSIONS.WORKSPACE_SETTINGS),
  },
  [CALLS_DEVELOPER]: {
    ...permission(ONE_TO_ONE_PERMISSIONS.CALLS_DEVELOPER_PAGE),
    ...permission(ONE_TO_ONE_PERMISSIONS.QUEUES_MANAGEMENT),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALLS_OPENING_HOURS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_SETTINGS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_STATS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_STATS_LEADERBOARD),
    ...permission(ONE_TO_ONE_PERMISSIONS.INTEGRATIONS_ONE_TO_ONE),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_LIST),
  },
  [CALLS_MANAGER]: {
    ...permission(ONE_TO_ONE_PERMISSIONS.CALLS_DEVELOPER_PAGE, [READ]),
    ...permission(ONE_TO_ONE_PERMISSIONS.QUEUES_MANAGEMENT),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALLS_OPENING_HOURS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_SETTINGS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_STATS),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_STATS_LEADERBOARD),
    ...permission(ONE_TO_ONE_PERMISSIONS.CALL_LIST),
    ...permission(ONE_TO_ONE_PERMISSIONS.LOCALIZATION_ONE_TO_ONE),
  },

  [CALLS_USER_ADMIN]: {
    ...permission(ONE_TO_ONE_PERMISSIONS.EDIT_USERS_ONE_TO_ONE),
  },

  [CALLS_AGENT]: {
    ...permission(ONE_TO_ONE_PERMISSIONS.CALLS_AGENT_TOOL),
  },
};

const DEFAULT_ROLE_PERMISSIONS_ONE_TO_MANY = {
  [SHOWS_OWNER]: {
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOWS),
    ...permission(ONE_TO_MANY_PERMISSIONS.LANGUAGE),
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOW_STATS),
    ...permission(ONE_TO_MANY_PERMISSIONS.EDIT_USERS_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.INTEGRATIONS_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.MODERATE),
    ...permission(ONE_TO_MANY_PERMISSIONS.MODERATE_CHAT_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.HIGHLIGHT_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.MANAGE_GAMES_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.GO_LIVE_IN_APP),
    ...permission(ONE_TO_MANY_PERMISSIONS.CHAT_TERMS),
    ...permission(ONE_TO_MANY_PERMISSIONS.BILLING_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.DATA_EXPORT_SERVICE_CONFIGURATION),
    ...permission(ONE_TO_MANY_PERMISSIONS.DATA_EXPORT_SERVICE_EXPORT_JOBS),
    ...permission(ONE_TO_MANY_PERMISSIONS.MANAGE_AI_GROUNDINGS),
    ...permission(ONE_TO_MANY_PERMISSIONS.MANAGE_AI_GENERATIVES),
    ...permission(ONE_TO_MANY_PERMISSIONS.LOCALIZATION_ONE_TO_MANY),
    ...permission(GENERAL_PERMISSIONS.WORKSPACE_SETTINGS),
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOWS_ACTIONS_MANAGE),
  },
  [SHOWS_ADMIN]: {
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOW_STATS),
    ...permission(ONE_TO_MANY_PERMISSIONS.INTEGRATIONS_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOWS_ACTIONS_MANAGE),
  },
  [SHOW_CREATOR]: {
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOWS),
    ...permission(ONE_TO_MANY_PERMISSIONS.MODERATE),
    ...permission(ONE_TO_MANY_PERMISSIONS.MODERATE_CHAT_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.HIGHLIGHT_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.MANAGE_GAMES_ONE_TO_MANY),
  },
  [MODERATOR]: {
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOWS, [READ]),
    ...permission(ONE_TO_MANY_PERMISSIONS.MODERATE),
    ...permission(ONE_TO_MANY_PERMISSIONS.MODERATE_CHAT_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.HIGHLIGHT_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.MANAGE_GAMES_ONE_TO_MANY),
    ...permission(ONE_TO_MANY_PERMISSIONS.SHOWS_ACTIONS_MANAGE),
  },
  [MANAGE_OWN_SHOWS]: {
    ...permission(ONE_TO_MANY_PERMISSIONS.GO_LIVE_IN_APP),
    ...permission(ONE_TO_MANY_PERMISSIONS.MANAGE_OWN_SHOWS),
  },
  [HOST]: {
    ...permission(ONE_TO_MANY_PERMISSIONS.GO_LIVE_IN_APP),
  },
};

const DEFAULT_ROLE_PERMISSIONS_VOD = {
  [VOD_ROLES.VOD_OWNER]: {
    ...permission(VOD_PERMISSIONS.VOD_MANAGE),
    ...permission(VOD_PERMISSIONS.EDIT_USERS_VOD),
    ...permission(VOD_PERMISSIONS.VOD_DISTRIBUTE),
    ...permission(VOD_PERMISSIONS.LOCALIZATION_VOD),
    ...permission(GENERAL_PERMISSIONS.WORKSPACE_SETTINGS),
    ...permission(VOD_PERMISSIONS.VOD_STATS),
    ...permission(VOD_PERMISSIONS.VOD_SETTINGS),
    ...permission(VOD_PERMISSIONS.VOD_INTEGRATIONS),
    ...permission(VOD_PERMISSIONS.VOD_ACTIONS_MANAGE),
  },
};

const DEFAULT_ROLE_PERMISSIONS_CHAT = {
  [CHAT_ROLES.CHAT_OWNER]: {
    ...permission(GENERAL_PERMISSIONS.WORKSPACE_SETTINGS),
    ...permission(CHAT_PERMISSIONS.CHAT_STATS),
    ...permission(CHAT_PERMISSIONS.EDIT_USERS_CHAT),
    ...permission(CHAT_PERMISSIONS.CHAT_SETTINGS),
    ...permission(CHAT_PERMISSIONS.CHAT_MANAGE_STORES),
    ...permission(CHAT_PERMISSIONS.CHAT_CMS_POSTS),
  },
  [CHAT_ROLES.CHAT_AGENT]: {
    ...permission(CHAT_PERMISSIONS.CHAT_APP),
  },
};

const DEFAULT_ROLE_PERMISSIONS = merge(
  DEFAULT_ROLE_PERMISSIONS_ONE_TO_ONE,
  DEFAULT_ROLE_PERMISSIONS_ONE_TO_MANY,
  DEFAULT_ROLE_PERMISSIONS_VOD,
  DEFAULT_ROLE_PERMISSIONS_CHAT,
);

const PERMISSIONS_TO_ROLE_MAP = permissionsToRoleMap(DEFAULT_ROLE_PERMISSIONS);

const PERMISSIONS = {
  ...ONE_TO_MANY_PERMISSIONS,
  ...ONE_TO_ONE_PERMISSIONS,
  ...VOD_PERMISSIONS,
  ...CHAT_PERMISSIONS,
  ...GENERAL_PERMISSIONS,
};
/** @typedef {typeof PERMISSIONS} Permissions */
/** @typedef {keyof Permissions} PermissionsKey */
/** @typedef {Permissions[PermissionsKey]} PermissionsValue */


module.exports = {
  ONE_TO_ONE_PERMISSIONS,
  ONE_TO_MANY_PERMISSIONS,
  DEFAULT_ROLE_PERMISSIONS_ONE_TO_ONE,
  DEFAULT_ROLE_PERMISSIONS_ONE_TO_MANY,
  DEFAULT_ROLE_PERMISSIONS_VOD,
  DEFAULT_ROLE_PERMISSIONS,
  PERMISSIONS_TO_ROLE_MAP,
  ROLES,
  BACKOFFICE_ROLES,
  ONE_TO_ONE_ROLES,
  ONE_TO_MANY_ROLES,
  VOD_ROLES,
  CHAT_ROLES,
  PERMISSIONS,
  BILLING_PERMISSIONS,
  VOD_PERMISSIONS,
  CHAT_PERMISSIONS,
  GENERAL_PERMISSIONS,
};
